// /restore (docs/08-app-design.md 5.2; app/art/welcome/PLAN.md sections 4.4
// and 5): 24 words with per-word validation and autocomplete, the chain
// read that decides what this phrase owns, the device passphrase, and the
// mandatory key refresh before the app is normal. The words go to the
// worker and are dropped here; only tree 0's public key comes back, with
// the address of the vault's fee reserve, which the words derive (the same
// on every device; docs/18-one-fee-reserve.md section 4), shown with its
// balance once the network check has passed.
// The ceremony frame carries the rail (Words, Check, Passphrase, Key
// refresh; when the chain says "No vault yet." the stops after Passphrase
// become the fee reserve and Activate, on /vault). The words step warns,
// where the mistake can happen, against another wallet's recovery phrase;
// on phones its Continue sits in a bar at the screen's foot and Clear moves
// up beside the heading, so one mis-tap cannot wipe 24 words. Browser Back
// during the save abandons it, as the way back would, unless installSetup
// has already begun; a Back after that leaves this page inert, even if a
// later read fails. Once the setup is saved, a step that fails after it
// says the setup is saved and resumes with it, never offering to discard it
// (R67). The key refresh step is never shown over a locked session: when the
// app locks on it (idle, a hidden tab, another tab's change), the page drops
// the engine it held and shows the saved screen with Unlock, and a line that
// says what is known of the refresh.

import { WORDLIST, type WorkerClient, snapshot, workerFeeSigner } from '@thevault/sdk';
import { type ClipboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { CeremonyFrame, LOCKED_WHILE_WAITING, SAVED_SETUP_UNREAD, type SavedNext, SetupSaved, SetupSaving, markSetupNotSaved, markSetupSavedAfter, markSetupSaving, reopenSaved, savedStepError, useSavedSetup } from '../components/Ceremony';
import { GasTankPanel } from '../components/GasTank';
import { KeyRefresh } from '../components/KeyRefresh';
import { RestoredStakeNote } from '../components/Staked';
import { PassphraseForm } from '../components/Passphrase';
import { Address, Amount, Notice, StatusLine } from '../components/primitives';
import { type Flow } from '../components/StepRail';
import { WRONG_NETWORK_SENTENCE, WrongNetwork, assertDeployment } from '../deployment';
import { type ChainState, type Engine, type RefreshProgress, type VaultInfo, vaultInfoFromGenesis } from '../engine';
import { type RelayerSigner, type Rpc, toBase58 } from '../rpc';
import { PROGRAM_ID, PROGRAM_ID_B58, session, useSession } from '../session';
import { SITE_DOMAIN } from '../site/facts';
import { installSetup } from '../storage';
import { explorerBase, explorerUrl, shortSig } from '../util';
import { probeFeeKey } from './Create';
import type { VaultSigner } from '@thevault/sdk';

// 'saved': the setup is saved on this device, then a later step failed (R67; SetupSaved).
type Step = 'words' | 'checking' | 'result' | 'passphrase' | 'saved' | 'refresh';
/**
 * The vault's fee reserve as the check step found it (docs/18-one-fee-reserve.md section 4): its balance, a balance
 * that could not be read (the flow goes on), or a network check that refused (2.5: no address and no balance shown).
 */
type FeeReserveReading = { lamports: bigint } | 'unreadable' | { wrongNetwork: true };
// 'absent' carries `prefundedLamports` when a deposit already reached this address before activation (R40): an
// empty, System-owned account, adopted by Initialize the same way Home's own prefunded state is. Every outcome's
// `info` carries `feeWallet`, the derived reserve's address.
type Outcome = ({ kind: 'absent'; info: VaultInfo; prefundedLamports?: bigint } | { kind: 'mismatch'; info: VaultInfo } | { kind: 'vault'; info: VaultInfo; treeIndex: number } | { kind: 'tombstone'; info: VaultInfo; treeIndex: number }) & {
  feeReserve: FeeReserveReading;
};

/** The network check of 2.5, then the reserve's balance. Never rejects: a refusal or a failed read is a reading. */
async function readFeeReserve(rpc: Rpc, address: string): Promise<FeeReserveReading> {
  try {
    await assertDeployment(rpc);
  } catch (e) {
    return e instanceof WrongNetwork ? { wrongNetwork: true } : 'unreadable';
  }
  try {
    return { lamports: await rpc.getBalance(address) };
  } catch {
    return 'unreadable';
  }
}

/** The result screen's fee reserve line (docs/18 section 4): the address only once the network check has passed. */
function FeeReserveFound({ address, reading }: { address: string; reading: FeeReserveReading }) {
  if (typeof reading === 'object' && 'wrongNetwork' in reading) {
    return (
      <StatusLine state="failed" word="Fee reserve not shown.">
        {WRONG_NETWORK_SENTENCE}
      </StatusLine>
    );
  }
  return (
    <p className="ink">
      Your fee reserve: <Address value={address} kind="fee reserve" full />
      {reading === 'unreadable' ? (
        ', its balance could not be read.'
      ) : (
        <>
          , holds <Amount raw={reading.lamports} decimals={9} symbol="SOL" />.
        </>
      )}
    </p>
  );
}

/** The stop each step is on the rail (StepRail's restore and restore-new flows). */
const STOP: Record<Step, number> = { words: 1, checking: 1, result: 2, passphrase: 3, saved: 3, refresh: 4 };

const WORDSET = new Set(WORDLIST);

/**
 * What this page knows of its key refresh attempts, over every attempt on this mount (Try again starts another one, and
 * its first event would otherwise hide that an earlier one was sent). `last` is the latest event of the latest attempt.
 */
type RefreshHistory = { last: RefreshProgress | null; landed: RefreshProgress | null; unknown: boolean; failedOnChain: boolean };
const NO_ATTEMPT: RefreshHistory = { last: null, landed: null, unknown: false, failedOnChain: false };

function withAttempt(h: RefreshHistory, p: RefreshProgress): RefreshHistory {
  return {
    last: p,
    landed: p.step === 'landed' ? p : h.landed,
    unknown: h.unknown || p.step === 'unknown',
    failedOnChain: h.failedOnChain || (p.step === 'failed' && p.kind === 'failed'),
  };
}

/** An attempt that has not settled: it may still be confirming a send (engine.ts refreshKey; the lock stops it at its next step otherwise). */
function inFlight(h: RefreshHistory): boolean {
  return h.last?.step === 'preparing' || h.last?.step === 'sending';
}

/**
 * How long Unlock waits, after a lock on the key refresh step, for an attempt still in flight: the confirmation's own
 * deadline (60 s, sdk/ts relayer.ts confirm) and a margin, so a send still confirming settles before /vault could send
 * the same refresh again, and a read that never answers cannot hold Unlock for ever.
 */
const UNLOCK_WAIT_MS = 75_000;

/**
 * The saved screen's line about the key refresh, after the app locked on its step. Built from every attempt, never from
 * the last event alone: landed, else unknown (any attempt that may have been sent), else failed on chain, else not
 * sent. While an attempt is still in flight it makes no final claim. `ended`: the device has nothing left to unlock
 * (another tab forgot it), so the sentences about what follows the unlock are left out.
 */
function RefreshAfterLock({ history, waiting, ended, rpcUrl }: { history: RefreshHistory; waiting: boolean; ended: boolean; rpcUrl: string }) {
  const tx = (signature: string | undefined): ReactNode => {
    if (!signature) return null;
    const url = explorerUrl(explorerBase(rpcUrl, 'tx'), signature);
    return (
      <>
        {' '}
        (transaction{' '}
        {url ? (
          <a href={url} target="_blank" rel="noreferrer">
            {shortSig(signature)} ↗
          </a>
        ) : (
          shortSig(signature)
        )}
        )
      </>
    );
  };
  let line: { state: 'landed' | 'pending' | 'failed' | 'neutral'; word: string; known: ReactNode; after?: string };
  if (history.landed) {
    line = history.landed.error
      ? { state: 'landed', word: 'Landed.', known: <>The key refresh landed{tx(history.landed.signature)}. This device could not record it before locking.</>, after: 'After you unlock, the vault looks this refresh up on chain. If it cannot confirm it there yet, the overview lists it as outcome unknown and asks for one more key refresh. It also asks for one if an action has landed under the new key since, or if you unlock more than about an hour after it landed.' }
      : { state: 'landed', word: 'Landed.', known: <>The key refresh landed{tx(history.landed.signature)} and this device recorded it.</> };
  } else if (waiting) {
    line = { state: 'pending', word: 'Stopping.', known: 'A key refresh was under way when the app locked. It is finishing now, and this line says what became of it once it has.' };
  } else if (history.unknown) {
    line = { state: 'neutral', word: 'Outcome unknown.', known: 'The key refresh was signed, and it may have been sent before the app locked; this page does not know whether it landed.', after: 'After you unlock, the vault is read on chain before the refresh is tried again.' };
  } else if (inFlight(history)) {
    // Unlock stopped waiting for it (UNLOCK_WAIT_MS)
    line = { state: 'neutral', word: 'Outcome unknown.', known: 'A key refresh was under way when the app locked and has not finished, so this page does not know whether it was sent.', after: 'After you unlock, the vault is read on chain before the refresh is tried again.' };
  } else if (history.failedOnChain) {
    line = { state: 'failed', word: 'Failed on chain.', known: 'The key refresh landed and failed on chain, so nothing moved.', after: 'After you unlock, the overview may still list it as not confirmed; the vault refreshes its key before your next action, or now from the overview.' };
  } else {
    line = { state: 'neutral', word: 'Not sent.', known: 'The key refresh was not sent, so the vault’s key is as it was.', after: 'After you unlock, the vault refreshes its key before your next action, or now from the overview.' };
  }
  return (
    <StatusLine state={line.state} word={line.word}>
      {line.known}
      {line.after && !ended ? ` ${line.after}` : null}
    </StatusLine>
  );
}

function WordInput({ index, value, onChange, onPaste }: { index: number; value: string; onChange: (v: string) => void; onPaste: (e: ClipboardEvent<HTMLInputElement>) => void }) {
  const [focus, setFocus] = useState(false);
  const clean = value.trim().toLowerCase();
  const valid = clean === '' || WORDSET.has(clean);
  const suggestions = focus && clean.length >= 2 && !WORDSET.has(clean) ? WORDLIST.filter((w) => w.startsWith(clean)).slice(0, 6) : [];
  return (
    <div className="word word-cell">
      <span>{index + 1}</span>
      <input
        id={`word-${index}`}
        aria-label={`Word ${index + 1}`}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        aria-invalid={valid ? 'false' : 'true'}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && suggestions.length === 1) onChange(suggestions[0]!);
        }}
      />
      {suggestions.length ? (
        <ul className="wordlist" role="listbox">
          {suggestions.map((s) => (
            <li key={s} role="option" aria-selected={false} onMouseDown={() => onChange(s)}>
              {s}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Restore() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>('words');
  const [words, setWords] = useState<string[]>(() => Array(24).fill(''));
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // `need` is the key refresh's live requirement, F + 15,000 (engine.refreshCost, docs/18 5.1 A), the panel's figure and its gate
  const [refresh, setRefresh] = useState<{ engine: Engine; signer: VaultSigner; funded: boolean; need: bigint } | null>(null);
  const runtime = useRef<{ worker: Worker; client: WorkerClient } | null>(null);
  const adopted = useRef(false);
  // R67: whether the setup is saved on this device (installSetup resolved), kept apart from the step shown and from
  // `busy`. From then on nothing is left to discard and nothing may save again: the way back goes, `seal` returns at
  // once, and a later failure lands on SetupSaved, whose one way forward resumes with this same setup.
  const saved = useRef(false);
  const [committed, setCommitted] = useState(false);
  const [postSave, setPostSave] = useState<{ error: string; next: SavedNext } | null>(null);
  /** The session re-reading storage after a failed adoption, so /locked finds the saved setup. */
  const relock = useRef<Promise<void> | null>(null);
  const { state: sessionState, settings } = useSession();
  // The app locked while the key refresh step was shown (or being prepared): the page has dropped the engine, its
  // signer and the fee reserve's key, and shows the saved screen with what it knows of the refresh. Set once; the ref
  // keeps a second caller (prepare's own check, the lock effect) from running the handler again.
  const [lockedOut, setLockedOut] = useState(false);
  const lockedOutRef = useRef(false);
  const [attempts, setAttempts] = useState<RefreshHistory>(NO_ATTEMPT);
  const [unlockWaitOver, setUnlockWaitOver] = useState(false);
  // A save another mount of this page load started (it is committing, or the session is opening it), or a setup one
  // saved that the session could not read back: no fresh start here (savedSetup.ts).
  const other = useSavedSetup();

  // A save in flight keeps its worker even if the page unmounts (browser Back is not held off by `busy`), so its own
  // awaits settle; it then checks `mounted` just before installSetup and, if the page has gone, abandons the save
  // there, as the way back would, and ends the worker itself. Past that check the save commits and the session adopts
  // the live worker (`adopted`).
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (!adopted.current && !saving.current) runtime.current?.worker.terminate();
    };
  }, []);

  const allValid = words.every((w) => WORDSET.has(w.trim().toLowerCase()));

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const parts = text.trim().toLowerCase().split(/\s+/);
    if (parts.length < 12) return;
    e.preventDefault();
    setWords(Array.from({ length: 24 }, (_, i) => parts[i] ?? ''));
    void navigator.clipboard?.writeText('').catch(() => {});
  };

  const check = async () => {
    setBusy(true);
    setError(null);
    setStep('checking');
    try {
      runtime.current?.worker.terminate();
      runtime.current = session.freshWorker();
      const { client } = runtime.current;
      // The capability probe (docs/18 2.4) before the words go in: the fee address below needs the same key import.
      await probeFeeKey(client);
      await client.adopt(words.map((w) => w.trim().toLowerCase()).join(' '));
      setWords(Array(24).fill(''));
      const genesis = await client.deriveHeldTree(PROGRAM_ID, 0);
      // The vault's one fee reserve, derived from the words (n = 0; docs/18 section 4): the same address on every
      // device, so a reserve funded elsewhere is found with its balance. Read beside the chain read, after the
      // network check of 2.5.
      const feeWallet = toBase58(await client.feeAddress(0));
      const info: VaultInfo = { ...(await vaultInfoFromGenesis(PROGRAM_ID_B58, genesis.verifyingKey)), feeWallet };
      const rpc = session.rpc();
      const [account, feeReserve] = await Promise.all([rpc.getAccountInfo(info.address), readFeeReserve(rpc, feeWallet)]);
      if (!account) {
        setOutcome({ kind: 'absent', info, feeReserve });
      } else {
        let snap;
        try {
          snap = snapshot(PROGRAM_ID, account.owner, account.data);
        } catch {
          // A deposit that arrived before activation is an empty, System-owned account (R40): adopted by
          // Initialize the same way Home's own prefunded state is, never refused as a foreign account.
          const systemOwned = account.owner.length === 32 && account.owner.every((b) => b === 0);
          if (systemOwned && account.data.length === 0) {
            setOutcome({ kind: 'absent', info, prefundedLamports: account.lamports, feeReserve });
            setStep('result');
            return;
          }
          setError('The address this phrase names holds an account that is not a vault of this program.');
          setStep('words');
          return;
        }
        const current = await client.deriveHeldTree(PROGRAM_ID, snap.treeIndex);
        const same = current.verifyingKey.length === snap.verifyingKey.length && current.verifyingKey.every((b, i) => b === snap.verifyingKey[i]);
        if (!same) setOutcome({ kind: 'mismatch', info, feeReserve });
        else setOutcome({ kind: snap.tag === 2 ? 'tombstone' : 'vault', info, treeIndex: snap.treeIndex, feeReserve });
      }
      setStep('result');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep('words');
    } finally {
      setBusy(false);
    }
  };

  /** A step after saving failed (R67): resume in place while the session holds the keys, else unlock the saved setup. */
  const afterSaveFailed = (error: string) => {
    if (session.get().state === 'unlocked') {
      setPostSave({ error, next: outcome?.kind === 'absent' ? 'continue' : 'retry' });
    } else {
      // The session does not hold the keys (adoption failed, or it has locked since): end this page's copy of them,
      // and let the session re-read storage, where the sealed setup now is, so /locked opens it.
      // The setup is stored but the session no longer holds it: guard the other screens until the re-read reaches
      // 'locked', so none offers a fresh start over the stored setup. After a failed adoption 'saving' holds while the
      // read runs if the user has left this page, and 'unread' is armed only once it has failed; with this page still
      // shown it is armed at once, so the pill agrees with the saved screen (savedSetup.ts markSetupSavedAfter).
      adopted.current = false;
      // the session ends a worker it adopted, after its own wipe; this ends one it never took (R68, session.discardWorker)
      if (runtime.current) session.discardWorker(runtime.current.worker);
      runtime.current = null;
      relock.current = markSetupSavedAfter(session.lock(), mounted.current);
      setPostSave({ error, next: 'unlock' });
    }
    setStep('saved');
  };

  /**
   * The app locked on the key refresh step, or while prepare() read what the step needs. Drop the closed engine, its
   * signer and the fee reserve's key, then take the reviewed R67 path to the saved screen (its worker discarded through
   * the session, I4; storage re-read; one way forward, Unlock). An attempt still running keeps reporting into
   * `attempts` (the saved screen's refresh line); nothing here acts on it.
   */
  const lockedOnRefresh = () => {
    if (lockedOutRef.current) return;
    lockedOutRef.current = true;
    setLockedOut(true);
    setRefresh(null);
    afterSaveFailed(LOCKED_WHILE_WAITING);
  };

  // The app locks itself (idle, or a hidden tab) while the saved screen offers Try again or Continue: say so at once,
  // and offer Unlock, rather than a Try again that could only fail. On the key refresh step the same: the step goes,
  // with the closed engine it held.
  useEffect(() => {
    if (sessionState === 'unlocked' || sessionState === 'starting') return;
    if (step === 'refresh') {
      lockedOnRefresh();
      return;
    }
    if (step !== 'saved' || busy || (postSave?.next !== 'retry' && postSave?.next !== 'continue')) return;
    afterSaveFailed(LOCKED_WHILE_WAITING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, busy, postSave, sessionState]);

  // After a lock on the key refresh step, Unlock waits while an attempt is still in flight (it may be confirming a
  // send that /vault would otherwise send again), for at most UNLOCK_WAIT_MS.
  const attemptInFlight = lockedOut && inFlight(attempts);
  useEffect(() => {
    if (!attemptInFlight) return;
    const timer = setTimeout(() => setUnlockWaitOver(true), UNLOCK_WAIT_MS);
    return () => clearTimeout(timer);
  }, [attemptInFlight]);
  const unlockWaiting = attemptInFlight && !unlockWaitOver;

  // This mount was let in while the device was empty (App.tsx RequireNoVault keeps rendering it after that) and has
  // saved nothing of its own, yet the session now holds or has read a setup: a save another mount of this page load
  // committed. Move on as the route guard would on entry, rather than run a fresh flow over the stored setup.
  useEffect(() => {
    if (saving.current || saved.current) return;
    if (sessionState === 'unlocked') navigate('/vault', { replace: true });
    else if (sessionState === 'locked') navigate('/welcome', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState]);

  // Everything after the setup is saved and adopted: the chain read, the signer, the fee reserve. Try again reruns it.
  const prepare = async () => {
    setBusy(true);
    // which step failed, in words, for the status line (savedStepError)
    let lead = 'The vault could not be read on chain.';
    try {
      const engine = session.engine();
      const state = (await engine.readChain()) as ChainState & { kind: 'vault' | 'tombstone' };
      lead = 'The vault’s key could not be opened on this device.';
      const signer = await engine.signerFor(state);
      lead = 'The fee reserve’s balance could not be read.';
      const cost = await engine.refreshCost();
      // The fee reserve read is RPC only and still answers after a lock: never go on to the refresh step with the
      // closed engine. (A page the user has left stays inert, as in the catch below.)
      if (session.get().state !== 'unlocked') {
        if (mounted.current) lockedOnRefresh();
        return;
      }
      setRefresh({ engine, signer, funded: cost.enough, need: cost.total });
      setPostSave(null);
      setStep('refresh');
    } catch (e) {
      // The page went while these reads ran (browser Back): the session already holds the setup, so a page the user has
      // left has nothing to report and must not act. Its re-read (session.lock()) and guard could otherwise land on
      // whatever the session holds by then, even a device forgotten meanwhile. The session's own state governs, and a
      // found vault's key refresh waits for the next action's own check (engine.ts, R44).
      if (!mounted.current) return;
      // once the app has locked, whatever the read threw (the bare "locked", a closed store), the lock is the cause
      afterSaveFailed(session.get().state === 'unlocked' ? savedStepError(e, lead) : LOCKED_WHILE_WAITING);
    } finally {
      setBusy(false);
    }
  };

  const seal = async (passphrase: string) => {
    if (saved.current || saving.current || !runtime.current || !outcome) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    const { client, worker } = runtime.current;
    // feeWallet is already in it (check); no seed is drawn and no fee key is sealed (docs/18 section 4)
    const info: VaultInfo = outcome.info;
    let relayer: RelayerSigner;
    let storing = false;
    try {
      // The probe again before anything is stored (docs/18 2.4), as Create does.
      await probeFeeKey(client);
      const sealed = await client.seal(passphrase);
      // The payer is installed here, before installSetup, not after it: adoptUnlocked takes its I3 token when called
      // (session.ts), so no await may sit between the save and the adoption, or a lock or forget in that gap would
      // go unseen. The installed key must be the one whose address the check step read (feeAddress(0)) and stores.
      const derived = await client.activateFeePayer(0);
      if (toBase58(derived) !== info.feeWallet) throw new Error('The fee reserve key does not match its derived address. Nothing was saved.');
      relayer = workerFeeSigner(client, derived);
      if (!mounted.current) {
        // The page went while the save ran (browser Back): leaving abandons the save, as the way back does. Nothing is
        // stored, so a page reached meanwhile (Welcome, or this flow entered afresh) stays right; end the worker.
        saving.current = false;
        worker.terminate();
        if (runtime.current?.worker === worker) runtime.current = null;
        return;
      }
      // From here the save is not abandoned: other screens show it as saving until the session holds it (savedSetup.ts).
      storing = true;
      markSetupSaving();
      // One transaction, refused if this device already holds a setup (R48).
      await installSetup({ sealedMaster: sealed, vaultInfo: info });
    } catch (e) {
      saving.current = false;
      if (storing) markSetupNotSaved();
      if (!mounted.current) {
        // the page went while the save ran, and nothing was saved: end the worker the unmount left running
        worker.terminate();
        if (runtime.current?.worker === worker) runtime.current = null;
        return;
      }
      // nothing is saved: the form stays, with Cancel
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      return;
    }
    // Saved. The session takes this worker next, so unmounting must no longer end it.
    saved.current = true;
    adopted.current = true;
    saving.current = false;
    setCommitted(true);
    try {
      // No await since installSetup resolved (I3); the worker already holds the fee reserve as its payer (above).
      await session.adoptUnlocked(client, worker, relayer, info);
    } catch (e) {
      afterSaveFailed(savedStepError(e, 'This page could not open the saved setup.'));
      setBusy(false);
      return;
    }
    // The page went while installSetup ran (browser Back after the last abandon point): the session holds the setup
    // now; Welcome, and a /create or /restore entered meanwhile, move on to /vault themselves. A found vault's key
    // refresh then waits for the next action's own check (engine.ts, R44), as it does when the refresh screen is left.
    // A Back during the reads below (prepare) leaves this page inert the same way: prepare's catch checks `mounted`.
    if (!mounted.current) return;
    if (outcome.kind === 'absent') {
      // /vault shows the rest of this flow's rail: the fee reserve, then Activate (Vault.tsx, SetupProgress)
      navigate('/vault', { state: { setupFlow: 'restore-new' } });
      return;
    }
    await prepare();
  };

  const resume = async () => {
    if (!postSave) return;
    const unlocked = session.get().state === 'unlocked';
    if (postSave.next === 'retry') return prepare();
    if (postSave.next === 'continue' && unlocked) return navigate('/vault', { state: { setupFlow: 'restore-new' } });
    setBusy(true);
    // Never to /welcome or /locked as if the device were empty: if the session cannot read the saved setup back, stay
    // here and offer a full reload (SavedNext 'reload').
    const where = await reopenSaved(relock.current);
    if (where === 'unread') {
      setPostSave({ error: SAVED_SETUP_UNREAD, next: 'reload' });
      setBusy(false);
      return;
    }
    navigate(where === 'locked' ? '/locked' : '/vault');
  };

  // The rail: four stops, or five once the chain says no vault exists yet (its fee reserve and Activate then follow
  // on /vault, as Create's do).
  const flow: Flow = outcome?.kind === 'absent' && (step === 'result' || step === 'passphrase' || step === 'saved') ? 'restore-new' : 'restore';
  const typing = step === 'words' || step === 'checking';
  // Two Clear buttons, one shown per layout (entrance.css), so each sits in the tab order where it sits on screen:
  // beside the heading on phones, after Continue on wider screens.
  const clearButton = (where: 'clear-ph' | 'clear-desk') => (
    <button type="button" className={`cta-quiet clear ${where}`} onClick={() => setWords(Array(24).fill(''))} disabled={busy || words.every((w) => !w)}>
      Clear
    </button>
  );
  // Words exist once one is typed, then in the worker until the setup is saved: until then the way back says it
  // discards them. From the moment the setup is saved (`committed`, whatever step shows, even after a later failure)
  // there is nothing to discard and no way back (R67).
  const back = committed || step === 'refresh' ? null : step === 'words' && words.every((w) => !w.trim()) ? 'welcome' : 'cancel';
  // The key refresh step, with the engine it holds, only while the session is unlocked and has not locked since: no
  // frame shows the closed engine, and its fee reserve panel can never start a refresh after a lock.
  const refreshLive = step === 'refresh' && refresh !== null && sessionState === 'unlocked' && !lockedOut;
  if (other === 'saving' ? !saving.current && !saved.current : other === 'unread' && !committed) {
    // another mount's save, still committing or being opened; or a setup saved and not read back (R67's double fault)
    return (
      <CeremonyFrame back={null} flow="restore" step={other === 'saving' ? 'saving' : 'unread'} current={STOP.saved} secret>
        {other === 'saving' ? <SetupSaving /> : <SetupSaved error={SAVED_SETUP_UNREAD} next="reload" busy={false} />}
      </CeremonyFrame>
    );
  }
  return (
    <CeremonyFrame
      back={back}
      flow={flow}
      // 'checking' is the words screen still: the frame keys its scroll and heading focus on the screen shown, and a
      // chain read that fails returns to the words with the error at the top
      step={typing ? (error ? 'words-error' : 'words') : step}
      // after a lock on the key refresh step the saved screen stays at that stop
      current={lockedOut ? STOP.refresh : STOP[step]}
      wide={typing}
      // the saved screen holds no secret, but while the session reports an empty device (its re-read of the saved setup
      // has not finished, or failed) the header's links would lead to a fresh Welcome; they come back once it reads it
      secret={step === 'saved' ? sessionState !== 'locked' && sessionState !== 'unlocked' : step !== 'refresh'}
      busy={busy && step === 'passphrase'}
      after={
        typing ? (
          <p className="xlink">
            No vault yet? <Link href="/create">Create one</Link>
          </p>
        ) : null
      }
    >
      {typing ? (
        <div className="words-step">
          <h1>Your 24 words.</h1>
          {clearButton('clear-ph')}
          <p className="lede">One word per field, in order. Pasting the whole phrase fills every field. Nothing leaves this device until you continue.</p>
          {error ? (
            <StatusLine state="failed" word="Not restored.">
              {error}
            </StatusLine>
          ) : null}
          <div className="guard">
            <Notice label="Only this app’s words." tone="accent">
              <p>
                Use only the 24 words this app gave you, never another wallet&rsquo;s recovery phrase. Type them only{' '}
                {SITE_DOMAIN ? (
                  <>
                    at <b>{SITE_DOMAIN}</b>
                  </>
                ) : (
                  'here'
                )}
                ; no one has a reason to ask for them.
              </p>
            </Notice>
          </div>
          <p className="b bal">Restoring runs a key refresh, one small transaction paid from your fee reserve. Use one device at a time.</p>
          <div className="phrase">
            {words.map((w, i) => (
              <WordInput key={i} index={i} value={w} onChange={(v) => setWords((ws) => ws.map((x, j) => (j === i ? v : x)))} onPaste={onPaste} />
            ))}
          </div>
          {/* on phones a bar at the screen's foot that holds only Continue; Clear sits by the heading there (clear-ph) */}
          <div className="sticky">
            <button type="button" className="cta cta-mint" onClick={() => void check()} disabled={!allValid || busy} aria-busy={busy ? 'true' : undefined}>
              {busy ? 'Reading the chain' : 'Continue'}
            </button>
          </div>
          {clearButton('clear-desk')}
        </div>
      ) : null}

      {step === 'result' && outcome ? (
        <>
          <h1>{outcome.kind === 'vault' ? 'Vault found.' : outcome.kind === 'tombstone' ? 'Vault closed on chain.' : outcome.kind === 'absent' ? 'No vault yet.' : 'Key mismatch.'}</h1>
          <Notice label="Address" tone={outcome.kind === 'mismatch' ? 'fail' : 'accent'}>
            <Address value={outcome.info.address} kind="vault" full />
            {outcome.kind === 'vault' ? <p className="ink">A vault exists at this address, on key tree {outcome.treeIndex}, and this phrase matches its current key.</p> : null}
            {outcome.kind === 'tombstone' ? <p className="ink">This vault was closed on chain. Reopening it refreshes its key; anything sent to the address since is still held there.</p> : null}
            {outcome.kind === 'absent' ? (
              <>
                <p className="ink">
                  {outcome.prefundedLamports ? (
                    <>
                      No vault exists for this phrase yet, but the address already holds <Amount raw={outcome.prefundedLamports} decimals={9} symbol="SOL" />. It is adopted when you activate the vault from the overview.
                    </>
                  ) : (
                    'No vault exists for this phrase yet. You can set this device up with it and activate the vault from the overview.'
                  )}
                </p>
                <p className="ink">If these words came from another wallet, stop here and create a new vault instead.</p>
              </>
            ) : null}
            {outcome.kind === 'mismatch' ? <p className="ink">This phrase does not match the vault's current key. It names the address, but another key tree holds the vault now. Check the words and try again.</p> : null}
          </Notice>
          {/* the mismatch outcome stops and shows nothing of the reserve (docs/18 section 4) */}
          {outcome.kind !== 'mismatch' && outcome.info.feeWallet ? <FeeReserveFound address={outcome.info.feeWallet} reading={outcome.feeReserve} /> : null}
          <div className="entry-actions">
            {outcome.kind !== 'mismatch' ? (
              <button type="button" className="cta cta-mint" onClick={() => setStep('passphrase')}>
                Continue
              </button>
            ) : null}
            <button
              type="button"
              className="cta cta-plain"
              onClick={() => {
                runtime.current?.worker.terminate();
                runtime.current = null;
                setOutcome(null);
                setStep('words');
              }}
            >
              Back to the words
            </button>
          </div>
        </>
      ) : null}

      {step === 'passphrase' ? (
        <>
          <h1>A passphrase for this device.</h1>
          <PassphraseForm onSubmit={(p) => void seal(p)} busy={busy} error={error} submitLabel="Restore" />
        </>
      ) : null}

      {step === 'saved' && postSave ? (
        <SetupSaved
          error={postSave.error}
          next={postSave.next}
          busy={busy || unlockWaiting}
          label={postSave.next === 'retry' ? 'reading the vault on chain to prepare its key refresh' : undefined}
          onNext={() => void resume()}
          detail={lockedOut ? <RefreshAfterLock history={attempts} waiting={unlockWaiting} ended={postSave.next === 'reload' || sessionState === 'no-vault'} rpcUrl={settings.rpcUrl} /> : undefined}
        />
      ) : null}

      {/* the one render between a lock and the effect that handles it: the saved screen, never the closed engine's step */}
      {step === 'refresh' && !refreshLive ? <SetupSaved error={LOCKED_WHILE_WAITING} next="unlock" busy /> : null}

      {refreshLive && refresh ? (
        <>
          <h1>Key refresh.</h1>
          <p className="lede">Your vault is found. One more step on this device: a key refresh.</p>
          <RestoredStakeNote engine={refresh.engine} />
          {refresh.funded ? (
            <KeyRefresh
              engine={refresh.engine}
              signer={refresh.signer}
              reason="Restoring on a new device always starts by refreshing the vault's key. One small transaction, paid from the fee reserve."
              onDone={() => navigate('/vault', { state: { setupFlow: 'restore', setupDone: true } })}
              onProgress={(p) => setAttempts((h) => withAttempt(h, p))}
            />
          ) : (
            <>
              {/* not "is empty": the gate is the F-aware threshold (docs/18 5.1), so a reserve funded elsewhere can hold SOL and still too little */}
              <p className="b">Restoring on a new device always starts by refreshing the vault&rsquo;s key. That takes one small transaction, and your fee reserve does not hold enough for it: add a little SOL to it below, and the refresh starts by itself. Your funds are not affected meanwhile.</p>
              <GasTankPanel engine={refresh.engine} needed={refresh.need} onFunded={() => setRefresh((r) => (r ? { ...r, funded: true } : r))} />
            </>
          )}
        </>
      ) : null}
    </CeremonyFrame>
  );
}
